<template>
    <div style="display: flex; justify-content: space-between">
        <Title>Definitions</Title>
        <Button
            v-if="isEditing"
            label="Add definition"
            icon="pi pi-plus"
            text
            size="small"
            severity="secondary"
            @click="openEntityDialog()"
        />
    </div>
    <div v-if="!entities?.length" class="empty-state">
        <i class="pi pi-building empty-icon"></i>
        <div>
            <h3>No definitions</h3>
            <p>
                <i
                    >To make sure everyone speaks the same language inside the
                    domain, definitions can be added here.</i
                >
            </p>
            <Button
                v-if="isEditing"
                label="Add Definition"
                icon="pi pi-plus"
                severity="primary"
                text
                @click="openEntityDialog()"
            />
        </div>
    </div>
    <DataTable
        ref="dataTable"
        v-else
        :loading="isLoading"
        :value="entities"
        selection-mode="single"
        dataKey="identity"
        :rowHover="true"
        :rowEditor="isEditing"
        @row-click="
            (event: DataTableRowClickEvent) => {
                const rowLink = `entity-link-${event.data.identity}`;
                dataTable.$el.querySelector(`#${rowLink}`)?.click();
            }
        "
    >
        <template #loading> Fetching data... </template>
        <Column field="name" header="Name" class="name-col">
            <template #body="{ data, field }">
                <RouterLink
                    :id="`entity-link-${data.identity}`"
                    class="entity-link"
                    :to="{
                        name: 'businessEntityPage',
                        params: {
                            orgName: route.params.orgName,
                            stekName: route.params.stekName,
                            businessEntityId: data.identity,
                        },
                    }"
                >
                    <div>
                        {{ data[field] }}
                    </div>
                </RouterLink>
            </template>
        </Column>
        <Column field="short_description" header="Description">
            <template #body="slotProps">
                <template v-if="slotProps.data.short_description">
                    {{ slotProps.data.short_description }}
                </template>
                <span
                    v-else
                    v-tooltip.top="'A description is not (yet) available'"
                >
                    ...
                </span>
            </template>
        </Column>
        <Column field="" header="" class="buttons-col">
            <template #body="{ index }" v-if="isEditing">
                <Button
                    severity="danger"
                    type="button"
                    text
                    rounded
                    icon="pi pi-trash"
                    :loading="isLoading"
                    @click="
                        (e: MouseEvent) => {
                            // Using stopPropagation will break confirm: https://github.com/primefaces/primevue/issues/5706
                            deleteBusinessEntity(e, index);
                        }
                    "
                />
            </template>
        </Column>
    </DataTable>
</template>

<script setup lang="ts">
import {
    useBusinessEntitiesStore,
    useBusinessEntitiesManager,
    BusinessEntity,
} from "@/stores/business-entities-store";
import { useConfirm } from "primevue/useconfirm";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import { DataTableRowClickEvent } from "primevue/datatable";
import { ref } from "vue";
import { useRoute } from "vue-router";
import Title from "@/components/Title.vue";

const props = defineProps<{
    isEditing: boolean;
    entities: BusinessEntity[];
}>();

const dataTable = ref();
const route = useRoute();

const { isLoading } = useBusinessEntitiesManager();
const { deleteEntity } = useBusinessEntitiesStore();
const { open: openEntityDialog } = useEditEntityDialog();

const confirm = useConfirm();

const deleteBusinessEntity = async (event: Event, index: number) => {
    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: `
            After deletion of '${props.entities[index].name}'
            as definition, you cannot reference it anymore. Are you sure?
        `,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            await deleteEntity(props.entities[index].identity);
        },
    });
};
</script>

<style scoped lang="scss">
.entity-link {
    text-decoration: none;
}

:deep(.p-datatable-thead) > tr > th,
:deep(.p-datatable-tbody) > tr > td {
    &.buttons-col {
        min-width: 50px;
        text-align: right;
    }
    &.name-col {
        width: 30rem;
    }
}
:deep(.p-datatable-tbody) > tr > td.buttons-col {
    padding-top: 0;
    padding-bottom: 0;
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}
</style>
